import { EmailInput as Ds5EmailInput } from "@fullscript/aviary-web";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { FloatingLabelInputProps } from "@aviary";
import { FloatingLabelInput } from "@aviary";
import { e2eAttributes } from "@shared/e2eAttributes";
import { l } from "@shared/locales/i18n";

import { isLengthValid, isEmailAtValid } from "./EmailValidation/EmailValidation";
import { SuggestedEmail } from "./SuggestedEmail/SuggestedEmail";
import { useSuggestedEmail } from "./useSuggestedEmail";

interface Props extends Omit<FloatingLabelInputProps, "id" | "label" | "type" | "errors"> {
  error?: string | JSX.Element;
  descriptiveText?: ReactNode;
  isLoading?: boolean;
  isOptional?: boolean;
  isDirty: boolean;
  hideSuggestion?: boolean;
  label?: string;
  value: string;
  setEmail: (value: string) => void;
  onSuggestedEmailClick?: (suggestedEmail: string) => void;
  /**
   * Opts this shared wrapper into the DS5 render path. Defaults to `"ds4"`
   * (legacy `FloatingLabelInput`), so existing call sites are visually
   * unchanged. When `"ds5"`, the same public prop contract renders the DS5
   * `EmailInput` from `@fullscript/aviary-web` instead. Flip per call site.
   */
  designSystemVersion?: "ds4" | "ds5";
}

const EmailInput = ({
  wrapperStyles,
  disabled,
  isLoading,
  descriptiveText,
  isDirty,
  hideSuggestion = false,
  error,
  setEmail,
  onSuggestedEmailClick,
  label = l.sharedCommon.Email,
  value,
  isOptional,
  designSystemVersion = "ds4",
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const { suggestedEmail, onEmailChange, clearSuggestedEmail } = useSuggestedEmail();

  const handleSuggestedEmailClick = () => {
    setEmail(suggestedEmail);
    clearSuggestedEmail();
    onSuggestedEmailClick?.(suggestedEmail);
  };

  const renderSuggestedEmail = () => {
    if (!suggestedEmail || hideSuggestion) return null;

    return (
      <SuggestedEmail
        suggestedEmail={suggestedEmail}
        handleSuggestedEmailClick={handleSuggestedEmailClick}
      />
    );
  };

  const getError = () => {
    if (isOptional && !isLengthValid(value)) return null;
    if (!isLengthValid(value)) {
      return t(l.sharedCommon.EmailRequired);
    }
    if (!isEmailAtValid(value)) {
      return t(l.sharedCommon.EmailMustHaveAt);
    }
    if (error) return error;

    return renderSuggestedEmail();
  };

  const getErrors = () => {
    if (!isDirty) return [renderSuggestedEmail()];

    return [getError()];
  };

  // DS5 `EmailInput` runs its own required/"@"/"."/regex validation onBlur, so the
  // wrapper defers format checks to DS5 here and only supplies the concerns DS5 does
  // not cover: the required-when-empty message, the caller-provided `error`, and the
  // fuzzy-match suggestion. Keeping the wrapper's "@" copy out of `errorCaption`
  // avoids a duplicate caption line, since DS5 appends its own validation message.
  const getDs5ErrorCaption = (): ReactNode[] => {
    if (!isDirty) return [renderSuggestedEmail()];

    if (isOptional && !isLengthValid(value)) return [null];
    if (!isLengthValid(value)) {
      return [t(l.sharedCommon.EmailRequired)];
    }
    if (error) return [error];

    return [renderSuggestedEmail()];
  };

  const onChange: FloatingLabelInputProps["onChange"] = e => {
    rest.onChange?.(e);
    onEmailChange(e.target.value);
  };

  if (designSystemVersion === "ds5") {
    return (
      <Ds5EmailInput
        id="email"
        name="email"
        maxLength={255}
        data-e2e={e2eAttributes.form.email}
        value={value}
        label={t(label)}
        disabled={disabled || isLoading}
        inputCaption={descriptiveText}
        customWrapperStyles={wrapperStyles}
        errorCaption={getDs5ErrorCaption()}
        {...rest}
        onChange={onChange}
      />
    );
  }

  return (
    <FloatingLabelInput
      maxLength={255}
      value={value}
      descriptiveText={descriptiveText}
      id="email"
      data-e2e={e2eAttributes.form.email}
      type="email"
      name="email"
      label={t(label)}
      wrapperStyles={wrapperStyles}
      errors={getErrors()}
      disabled={disabled || isLoading}
      {...rest}
      onChange={onChange}
    />
  );
};
export { EmailInput };
